import type { CSSProperties } from 'react';

// StatusDot — five-state status indicator. Shape AND colour together carry
// meaning per the design philosophy:
//   online   → filled green dot (state-online)
//   warning  → filled amber dot (state-warning)
//   blocked  → filled red dot (state-blocked) — also used for "overdue"
//   pending  → hollow ring with ink-tertiary border
//   changed  → filled ore dot (accent) — used to mark recent changes
// The default size is 6px to match the global `.dot-*` recipes; callers can
// override for larger or smaller contexts (e.g. a 10px dot in the auth wordmark).
export type StatusState = 'online' | 'warning' | 'blocked' | 'pending' | 'changed';

type StatusDotProps = {
  state: StatusState;
  size?: number;
  className?: string;
  title?: string;
};

// Map each state to the matching global recipe so the dark-theme glow on
// online/blocked dots applies via the `.dot-*` selectors in global.css.
const STATE_TO_RECIPE: Record<StatusState, string> = {
  online: 'dot-online',
  warning: '',
  blocked: 'dot-blocked',
  pending: 'dot-pending',
  changed: 'dot-accent',
};

export function StatusDot({ state, size = 6, className, title }: StatusDotProps) {
  const style: CSSProperties = {
    width: size,
    height: size,
    borderRadius: '999px',
    display: 'inline-block',
    flexShrink: 0,
  };

  // Only the warning state has no global recipe — apply its colour inline.
  if (state === 'warning') {
    style.background = 'var(--state-warning)';
  }

  const cls = [STATE_TO_RECIPE[state], className].filter(Boolean).join(' ');
  return <span className={cls} style={style} title={title} aria-label={state} />;
}
